import React from 'react';
import { Form, Upload, Switch, Typography, Radio, InputNumber, Row, Col, ColorPicker } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomTooltip from '../../Components/Modals/CustomTooltip';
import Tutorial from './Tutorial';
import BorderRadiusControl from '../BorderRadiusControl';

const { Title, Text } = Typography;
const InfoIcon = InfoCircleOutlined;



const BannerStep = ({ form, formData, handlePreview, handleFormChange, UploadFileIcon }) => {

    return (
        <Row gutter={32} style={{ minHeight: 400 }}>
            {/* Left: Form */}
            <Col xs={24} md={12}>
                <div style={{ padding: '1.25rem 1rem' }}>
                    <Title level={4}>Banner Settings</Title>
                    <Text type="secondary">
                        Configure the banner for your catalogue.
                    </Text>

                    <Form
                        form={form}
                        layout="vertical"
                        style={{ marginTop: '1.5rem' }}
                        onValuesChange={handleFormChange}
                    >
                        <Form.Item
                            name={['banner', 'logo']}
                            label={<>
                                Logo
                                <CustomTooltip title="Upload your logo">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <Upload
                                listType="picture-card"
                                maxCount={1}
                                beforeUpload={() => false} // Prevent auto upload
                                fileList={formData.banner && formData.banner.logo ? (() => {
                                    // Ensure we have a proper file list object
                                    const logo = formData.banner.logo;
                                    // If it's already a File object or has originFileObj
                                    if (logo instanceof File || (logo.originFileObj instanceof File)) {
                                        return [logo];
                                    }
                                    // If it's a string URL
                                    if (typeof logo === 'string' || typeof logo.url === 'string') {
                                        return [{
                                            uid: '-1',
                                            name: 'logo.png',
                                            status: 'done',
                                            url: typeof logo === 'string' ? logo : logo.url
                                        }];
                                    }
                                    return [];
                                })() : []}
                                onPreview={() => {
                                    if (formData.banner && formData.banner.logo) {
                                        handlePreview(formData.banner.logo);
                                    }
                                }}
                                onChange={({ fileList }) => {
                                    const newLogo = fileList.length > 0 ? fileList[0] : null;

                                    // Update form data using handleFormChange
                                    const newBannerData = { ...formData.banner, logo: newLogo };
                                    handleFormChange({ banner: newBannerData });

                                }}
                            >
                                <div>
                                    <UploadFileIcon />
                                    <div style={{ marginTop: '0.5rem' }}>Upload</div>
                                </div>
                            </Upload>
                        </Form.Item>

                        <Form.Item
                            name={['customization', 'bannerBackgroundColor']}
                            label={<>
                                Banner Background Color
                                <CustomTooltip title="Background color for the banner when no background image is set">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <ColorPicker
                                value={formData.customization?.bannerBackgroundColor}
                                onChange={(color) => {
                                    const newCustomizationData = {
                                        ...formData.customization,
                                        bannerBackgroundColor: color
                                    };
                                    handleFormChange({ customization: newCustomizationData });
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            name={['banner', 'heightOption']}
                            label={<>
                                Desktop Banner Height (px)
                                <CustomTooltip title="Set the height of the banner on desktop">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <Radio.Group
                                onChange={(e) => {
                                    const value = e.target.value;
                                    const newBannerData = { ...formData.banner, heightOption: value };

                                    // Se non è custom, aggiorna anche l'altezza effettiva
                                    if (value !== 'custom') {
                                        newBannerData.height = parseInt(value, 10);
                                        newBannerData.coverHeightDesktop = parseInt(value, 10);
                                    }

                                    handleFormChange({ banner: newBannerData });
                                }}
                                value={formData.banner?.heightOption || '100'}
                            >
                                <Radio value="100">100</Radio>
                                <Radio value="120">120</Radio>
                                <Radio value="150">150</Radio>
                                <Radio value="200">200</Radio>
                                <Radio value="custom">Custom</Radio>
                            </Radio.Group>

                            {formData.banner?.heightOption === 'custom' && (
                                <div style={{ marginTop: '0.5rem' }}>
                                    <InputNumber
                                        min={50}
                                        max={500}
                                        value={formData.banner?.height}
                                        onChange={(value) => {
                                            const newBannerData = {
                                                ...formData.banner,
                                                height: value,
                                                coverHeightDesktop: value
                                            };
                                            handleFormChange({ banner: newBannerData });
                                        }}
                                        style={{ width: '100%' }}
                                        placeholder="Enter height in pixels"
                                    />
                                </div>
                            )}
                        </Form.Item>

                        <Form.Item
                            name={['banner', 'heightMobileOption']}
                            label={<>
                                Mobile Banner Height (px)
                                <CustomTooltip title="Set the height of the banner on mobile">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <Radio.Group
                                onChange={(e) => {
                                    const value = e.target.value;
                                    const newBannerData = { ...formData.banner, heightMobileOption: value };

                                    // Se non è custom, aggiorna anche l'altezza effettiva
                                    if (value !== 'custom') {
                                        newBannerData.heightMobile = parseInt(value, 10);
                                        newBannerData.coverHeightMobile = parseInt(value, 10);
                                    }

                                    handleFormChange({ banner: newBannerData });
                                }}
                                value={formData.banner?.heightMobileOption || '100'}
                            >
                                <Radio value="100">100</Radio>
                                <Radio value="120">120</Radio>
                                <Radio value="150">150</Radio>
                                <Radio value="200">200</Radio>
                                <Radio value="custom">Custom</Radio>
                            </Radio.Group>

                            {formData.banner?.heightMobileOption === 'custom' && (
                                <div style={{ marginTop: '0.5rem' }}>
                                    <InputNumber
                                        min={50}
                                        max={500}
                                        value={formData.banner?.heightMobile}
                                        onChange={(value) => {
                                            const newBannerData = {
                                                ...formData.banner,
                                                heightMobile: value,
                                                coverHeightMobile: value
                                            };
                                            handleFormChange({ banner: newBannerData });
                                        }}
                                        style={{ width: '100%' }}
                                        placeholder="Enter height in pixels"
                                    />
                                </div>
                            )}
                        </Form.Item>

                        <Form.Item
                            name={['banner', 'logoPosition']}
                            label={<>
                                Logo and Name Position
                                <CustomTooltip title="Position of the logo and name within the banner">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <Radio.Group
                                onChange={(e) => {
                                    const newLogoPosition = e.target.value;
                                    const newBannerData = {
                                        ...formData.banner,
                                        logoPosition: newLogoPosition
                                    };
                                    handleFormChange({ banner: newBannerData });
                                }}
                                value={formData.banner.logoPosition}
                            >
                                <Radio value="left">Left</Radio>
                                <Radio value="center">Center</Radio>
                                <Radio value="right">Right</Radio>
                            </Radio.Group>
                        </Form.Item>

                        <Form.Item
                            label={<>
                                Don't Show Logo
                                <CustomTooltip title="When enabled, the logo will not be displayed in the banner">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <Switch
                                disabled={!formData.banner?.logo}
                                checked={!(formData.banner?.isLogoVisible ?? true)}
                                onChange={(checked) => {
                                    const newBannerData = {
                                        ...formData.banner,
                                        isLogoVisible: !checked
                                    };
                                    handleFormChange({ banner: newBannerData });
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={<>
                                Don't Show Name
                                <CustomTooltip title="When enabled, the catalogue name will not be displayed in the banner">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <Switch
                                checked={!(formData.banner?.isTitleVisible ?? true)}
                                onChange={(checked) => {
                                    const newBannerData = {
                                        ...formData.banner,
                                        isTitleVisible: !checked
                                    };
                                    handleFormChange({ banner: newBannerData });
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="bannerImageDesktop"
                            label={<>
                                Banner Image (Desktop)
                                <CustomTooltip title="Image to use as background for the desktop banner">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <Upload
                                listType="picture-card"
                                maxCount={1}
                                beforeUpload={() => false} // Prevent auto upload
                                fileList={formData.banner && formData.banner.bannerImage ? (() => {
                                    // Ensure we have a proper file list object
                                    const img = formData.banner.bannerImage;
                                    // If it's already a File object or has originFileObj
                                    if (img instanceof File || (img.originFileObj instanceof File)) {
                                        return [img];
                                    }
                                    // If it's a string URL
                                    if (typeof img === 'string' || typeof img.url === 'string') {
                                        return [{
                                            uid: '-1',
                                            name: 'banner-desktop.png',
                                            status: 'done',
                                            url: typeof img === 'string' ? img : img.url
                                        }];
                                    }
                                    return [];
                                })() : []}
                                onPreview={() => {
                                    if (formData.banner && formData.banner.bannerImage) {
                                        handlePreview(formData.banner.bannerImage);
                                    }
                                }}
                                onChange={({ fileList }) => {
                                    const newBannerImage = fileList.length > 0 ? fileList[0] : null;

                                    // Update form data using handleFormChange
                                    const newBannerData = { ...formData.banner, bannerImage: newBannerImage };
                                    handleFormChange({ banner: newBannerData });

                                }}
                            >
                                <div>
                                    <UploadFileIcon />
                                    <div style={{ marginTop: '0.5rem' }}>Upload</div>
                                </div>
                            </Upload>
                        </Form.Item>

                        <Form.Item
                            name="bannerImageMobile"
                            label={<>
                                Banner Image (Mobile)
                                <CustomTooltip title="Image to use as background for the mobile banner">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <Upload
                                listType="picture-card"
                                maxCount={1}
                                beforeUpload={() => false} // Prevent auto upload
                                fileList={formData.banner && formData.banner.bannerImageMobile ? (() => {
                                    // Ensure we have a proper file list object
                                    const img = formData.banner.bannerImageMobile;
                                    // If it's already a File object or has originFileObj
                                    if (img instanceof File || (img.originFileObj instanceof File)) {
                                        return [img];
                                    }
                                    // If it's a string URL
                                    if (typeof img === 'string' || typeof img.url === 'string') {
                                        return [{
                                            uid: '-1',
                                            name: 'banner-mobile.png',
                                            status: 'done',
                                            url: typeof img === 'string' ? img : img.url
                                        }];
                                    }
                                    return [];
                                })() : []}
                                onPreview={() => {
                                    if (formData.banner && formData.banner.bannerImageMobile) {
                                        handlePreview(formData.banner.bannerImageMobile);
                                    }
                                }}
                                onChange={({ fileList }) => {
                                    const newBannerImageMobile = fileList.length > 0 ? fileList[0] : null;

                                    // Update form data using handleFormChange
                                    const newBannerData = { ...formData.banner, bannerImageMobile: newBannerImageMobile };
                                    handleFormChange({ banner: newBannerData });

                                }}
                            >
                                <div>
                                    <UploadFileIcon />
                                    <div style={{ marginTop: '0.5rem' }}>Upload</div>
                                </div>
                            </Upload>
                        </Form.Item>

                        <Form.Item>
                            <label style={{ display: 'block', marginBottom: 8 }}>
                                Banner Corner Radius
                                <CustomTooltip title="Set the border radius for the corners of the catalog banner/header">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </label>
                            <BorderRadiusControl
                                prefix="banner"
                                topLeft={formData.customization?.bannerCornerTopLeft}
                                topRight={formData.customization?.bannerCornerTopRight}
                                bottomRight={formData.customization?.bannerCornerBottomRight}
                                bottomLeft={formData.customization?.bannerCornerBottomLeft}
                                onChange={(values) => handleFormChange({ customization: values })}
                            />
                        </Form.Item>
                    </Form>
                </div>
            </Col>
            {/* Right: Tutorial */}
            <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start' }}>
                <Tutorial
                    title="Banner Settings"
                    description={
                        <>
                            Set up the main banner for your catalogue.<br />
                            You can upload separate images for desktop and mobile, adjust banner height, and choose the position of your logo and name.<br /><br />
                            <b>Image Guidelines:</b>
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                <li><strong>Logo:</strong> 500 × 500 pixels (square), PNG/JPEG, max 10 MB. Use square layout for compatibility</li>
                                <li><strong>Desktop Banner:</strong> 1920 × 600 pixels (landscape), PNG/JPEG, max 10 MB. Keep content in central area</li>
                                <li><strong>Mobile Banner:</strong> 800 × 600 pixels (portrait/square-friendly), PNG/JPEG, max 10 MB. Avoid content near edges</li>
                            </ul>
                            <br />
                            <b>Tips:</b>
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                <li>Use high-quality images for best results on all devices</li>
                                <li>Keep branding elements (logo/name) visible and well-positioned</li>
                                <li>Test both desktop and mobile views for responsiveness</li>
                                <li>Use transparent PNG backgrounds for logos when possible</li>
                            </ul>
                        </>
                    }
                />
            </Col>
        </Row>
    );
};

export default BannerStep;
